import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Siren, Loader2, HeartHandshake, Facebook, Linkedin, Star } from "lucide-react";
import { PageHero, SectionHeading, Reveal, usePageMeta } from "@/components/kit";
import { HERO_BG, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO, ADDRESS, HOURS_WEEK, HOURS_TIME, AFTER_HOURS, FACEBOOK_URL, LINKEDIN_URL, GOOGLE_REVIEW_URL } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-[#0D0D10] border border-gold/20 rounded-sm px-4 py-3.5 text-sm text-[#F4EFE6] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/30 transition-colors";

const INFO = [
  { icon: Phone, label: "Phone", value: PHONE, href: PHONE_TEL, testid: "contact-phone-card", note: "Fastest way to reach us" },
  { icon: Mail, label: "Email", value: EMAIL, href: EMAIL_MAILTO, testid: "contact-email-card", note: "We reply within one business day" },
  { icon: MapPin, label: "Address", value: ADDRESS, testid: "contact-address-card", note: "Serving all of Middle Tennessee" },
  { icon: Clock, label: "Business Hours", value: `${HOURS_WEEK} · ${HOURS_TIME}`, testid: "contact-hours-card", note: AFTER_HOURS },
];

export default function ContactPage() {
  usePageMeta(
    "Contact Us | Redeemer Solutions LLC — Christiana, TN",
    "Call (615) 987-0139 or email Redeemer Solutions LLC in Christiana, TN. Open Monday–Friday 7 AM–4:30 PM with after-hours emergency service."
  );
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — thank you. We'll respond shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at (615) 987-0139.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        testid="contact-hero"
        eyebrow="Contact Us"
        lines={["Let's Start the", { t: "Conversation", gold: true }]}
        sub="Questions, quotes, emergencies — every inquiry gets a real answer from a real person."
        image={HERO_BG}
        imageAlt="Contact Redeemer Solutions LLC"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {INFO.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.07}>
              <div data-testid={c.testid} className="bg-[#121216] border border-gold/15 rounded-sm p-7 gold-glow-card h-full">
                <c.icon size={20} className="text-gold" />
                <p className="font-mono-label text-[10px] uppercase tracking-[0.24em] text-zinc-500 mt-4">{c.label}</p>
                {c.href ? (
                  <a href={c.href} className="block text-sm font-semibold text-[#F4EFE6] mt-2 hover:text-gold-champagne transition-colors break-all">{c.value}</a>
                ) : (
                  <p className="text-sm font-semibold text-[#F4EFE6] mt-2">{c.value}</p>
                )}
                <p className="text-xs text-zinc-500 mt-2">{c.note}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-6">
          <a
            href={PHONE_TEL}
            data-testid="contact-emergency-banner"
            className="flex flex-col sm:flex-row items-center justify-center gap-4 border border-red-500/30 bg-red-500/5 rounded-sm px-8 py-6 text-center hover:bg-red-500/10 transition-colors"
          >
            <Siren size={22} className="text-red-400 shrink-0" />
            <p className="text-sm text-zinc-300">
              <span className="text-red-400 font-semibold">System down right now?</span> After-hours and weekend emergency
              dispatch — call {PHONE} any time.
            </p>
          </a>
        </Reveal>

        <Reveal className="mt-6">
          <Link
            to="/prayer-requests"
            data-testid="contact-prayer-link"
            className="flex flex-col sm:flex-row items-center justify-center gap-4 border border-gold/25 bg-gold/5 rounded-sm px-8 py-6 text-center hover:bg-gold/10 transition-colors"
          >
            <HeartHandshake size={22} className="text-gold shrink-0" />
            <p className="text-sm text-zinc-300">
              <span className="text-gold-champagne font-semibold">Need prayer?</span> Send us a prayer
              request — we read and pray over every one, personally.
            </p>
          </Link>
        </Reveal>

        <Reveal className="mt-6">
          <div data-testid="contact-social-row" className="flex flex-col sm:flex-row items-center justify-center gap-4 border border-gold/15 bg-[#121216] rounded-sm px-8 py-6">
            <p className="font-mono-label text-[11px] uppercase tracking-[0.2em] text-gold-champagne">Like · Share · Follow!</p>
            <div className="flex items-center gap-3">
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook-link" aria-label="Redeemer Solutions on Facebook" className="w-9 h-9 rounded-sm border border-gold/25 flex items-center justify-center text-gold hover:bg-gold/10 hover:border-gold/50 transition-colors">
                <Facebook size={16} />
              </a>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" aria-label="Redeemer Solutions on LinkedIn" className="w-9 h-9 rounded-sm border border-gold/25 flex items-center justify-center text-gold hover:bg-gold/10 hover:border-gold/50 transition-colors">
                <Linkedin size={16} />
              </a>
              <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-review-link" className="inline-flex items-center gap-2 h-9 px-4 rounded-sm border border-gold/25 text-gold font-mono-label text-[10px] uppercase tracking-[0.16em] hover:bg-gold/10 hover:border-gold/50 transition-colors">
                <Star size={14} /> Leave us a review
              </a>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mt-20 items-start">
          <div>
            <SectionHeading
              eyebrow="Send a Message"
              title="We read every note personally."
              sub="Prefer writing it down? Send us the details and we'll follow up by phone or email — whichever you prefer."
              testid="contact-form-heading"
            />
          </div>
          <Reveal delay={0.08}>
            <form data-testid="contact-form" onSubmit={submit} className="bg-[#121216] border border-gold/15 rounded-sm p-7 sm:p-9 space-y-5">
              <div>
                <label htmlFor="contact-name" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Name *</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-email" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Email *</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Phone</label>
                  <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(615) 555-0123" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-message" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Message *</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="How can we help?" />
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={sending}
                className="inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-semibold text-sm tracking-wide px-10 py-4 rounded-sm transition-[transform,box-shadow,opacity] duration-300 hover:scale-[1.02] hover:shadow-[0_0_32px_rgba(212,175,55,0.35)] disabled:opacity-60 disabled:hover:scale-100"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
